/**
 * @file Link — a styled inline text link
 * @version 0.1.0
 */

import React, { forwardRef, type AnchorHTMLAttributes } from 'react';
import { isDev } from '../../../utils/env';
import { styled } from '@mui/material/styles';
import { tokens, componentTokens } from '../../../theme/tokens';
import { Icon } from '../Icon/Icon';
import type { StandardIconName } from '../Icon/Icon.Standard';
import { SOCIAL_ICON_NAMES, type SocialIconName } from '../Icon/Icon.Social';

// @render-mode: vite
// MUI/styled-based primitive (styling lives in theme/components.ts — no
// variant library, no CSS Modules); the marker above opts into the
// Vite-spawn render harness used for visual regression checks.

/*
 * Link
 *
 * 35 Figma cells: 5 types × 2 colors (asymmetric) × 5 states.
 *   Type:  caret-left | caret-right | social-icon | text-light | text-medium
 *   Color: dark (on-light-surface) | white (on-dark-surface) — caret types only;
 *          social-icon + text-light + text-medium ship dark only.
 *   State: default · hover · active · focus · disabled. Hover ≡ Active visually
 *          (collapsed to a single CSS rule). Focus has a 1px border ring (no
 *          background fill); hover/active have a background fill (no border).
 *
 * Disabled renders as <span aria-disabled="true"> instead of <a> — per CATALOG
 * a11y note: native <a> cannot truly be disabled.
 *
 * Underline behaviour (text-light + text-medium types):
 *   - text-medium: underline color tracks the text color via currentColor.
 *   - text-light + color="dark":  underline is ALWAYS Dark-Blue (Figma quirk
 *                                 preserved — including disabled state).
 *   - text-light + color="white": underline follows currentColor so it renders
 *                                 white on dark surfaces.
 *
 * Caret types map to chevron-* assets in the standard icon library
 * (Figma names "caret-left"/"caret-right" → chevron-left/chevron-right).
 */

const l = componentTokens.link;

export type LinkVariant =
  | 'caret-left'
  | 'caret-right'
  | 'social-icon'
  | 'text-light'
  | 'text-medium';

export type LinkColor = 'dark' | 'white';

export type { SocialIconName };

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'> {
  /** Figma variant — selects icon affordance + text weight. */
  variant: LinkVariant;
  /** Surface treatment. `dark` (default) for on-light surfaces; `white` for on-dark surfaces. */
  color?: LinkColor;
  /** Destination URL. REQUIRED — primitive does NOT fall back to "#". */
  href: string;
  /** Renders as <span aria-disabled="true"> with disabled visual treatment. */
  disabled?: boolean;
  /** Social brand mark. REQUIRED when variant === "social-icon". */
  socialIcon?: SocialIconName;
  /** Override the caret glyph for `caret-left` / `caret-right` variants.
   *  Accepts any name in the Icon.Standard 16px (`sm`) registry — see
   *  `src/assets/icons/standard/sm/*.svg`. Examples: "chevron-right" (default
   *  for caret-right), "arrow-right", "arrows-vertical". Defaults to
   *  `chevron-left` / `chevron-right`. Ignored for non-caret variants. */
  iconName?: StandardIconName;
  /** Provide a custom React node for the icon slot (overrides `iconName`).
   *  Use when the icon needs runtime behavior the static `iconName` registry
   *  can't express — e.g., a motion-animated glyph that morphs between
   *  states. Must render at 16×16 and use `currentColor` for stroke/fill so
   *  it inherits the Link's theme-aware text color. Ignored for non-caret /
   *  non-social variants. */
  icon?: React.ReactNode;
}

interface RootProps {
  $variant: LinkVariant;
  $color: LinkColor;
  $disabled?: boolean;
}

const Root = styled('a', {
  shouldForwardProp: (prop) =>
    prop !== '$variant' && prop !== '$color' && prop !== '$disabled',
})<RootProps>(({ $variant, $color, $disabled }) => {
  const isText = $variant === 'text-light' || $variant === 'text-medium';
  const hasIcon = $variant !== 'text-light' && $variant !== 'text-medium';

  // Foreground resolution.
  const fg = $disabled
    ? l.fgDisabled
    : $color === 'white'
      ? l.fgDark
      : $variant === 'text-light'
        ? l.fgSubtleBright
        : l.fgEmphasisBright;

  // Surface-aware hover/focus.
  const bgHover = $color === 'white' ? l.bgHoverDark : l.bgHoverBright;
  const focusRing = $color === 'white' ? l.focusRingDark : l.focusRingBright;

  const fontWeight = $variant === 'text-light' ? 300 : 500;

  return {
    // Layout — inline-flex matches Figma's COMPONENT_SET layout.
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    height: 24,
    padding: 0,
    borderRadius: 100, // matches Figma's pill background-radius for caret/social-icon.
    boxSizing: 'border-box',
    position: 'relative',

    // Anchor reset + Figma typography.
    textDecoration: 'none',
    cursor: $disabled ? 'default' : 'pointer',
    color: fg,
    fontFamily: tokens.font['family-avenir'],
    fontSize: 16,
    fontWeight,
    lineHeight: '24px',

    // Create a stacking context so ::before's z-index: -1 stays inside Root
    // and renders ABOVE the parent container's background (otherwise it
    // escapes upward and hides behind ancestor elements).
    isolation: 'isolate',

    // Caret/social-icon: rounded-pill background appears on hover/active, 1px
    // border ring on focus. Pill OVERFLOWS the inline-flex bounding box per
    // per-variant Figma Background offsets (extracted from selection.json
    // — asymmetric for carets since icon-side gets less padding):
    //   caret-left:  -6 left, -10 right, -2 top, -2 bottom
    //   caret-right: -10 left, -6 right, -2 top, -2 bottom
    //   social-icon: -8 left, -8 right, -2 top, -2 bottom
    // Rendered as a ::before pseudo-element with z-index: -1 so text/chevron
    // stack on top. Text variants have no pill — only the underline.
    ...(hasIcon &&
      !$disabled && {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -2,
          bottom: -2,
          left: $variant === 'caret-left' ? -6 : $variant === 'social-icon' ? -8 : -10,
          right: $variant === 'caret-left' ? -10 : $variant === 'social-icon' ? -8 : -6,
          borderRadius: 100,
          zIndex: -1,
          backgroundColor: 'transparent',
          border: '1px solid transparent',
          boxSizing: 'border-box',
        },
        // Hover + Active share a visual (pill background). Per new-primitive
        // SKILL Step 11: each state activates on its real pseudo-class OR its
        // data-force attribute (so the catalog matrix can render either at rest).
        // Real :hover/:active are guarded against ALL force-* attributes so a
        // forced-state demo cell never stacks a second state on real mouseover.
        '&[data-force-hover]::before, &[data-force-active]::before, &:not([data-force-focus]):not([data-force-hover]):not([data-force-active]):hover::before, &:not([data-force-focus]):not([data-force-hover]):not([data-force-active]):active::before': {
          backgroundColor: bgHover,
        },
        '&:focus-visible::before, &[data-force-focus]::before': {
          borderColor: focusRing,
        },
        '&:focus-visible, &[data-force-focus]': {
          outline: 'none',
        },
      }),

    // Text variants — underline rendered as ::after, 1px tall, full width.
    // HIDDEN by default — per Figma source, the underline appears ONLY on
    // :hover / :active (opacity 1) and stays hidden on Default/Focus/Disabled
    // (opacity 0). Both text-light and text-medium share this pattern. Color
    // logic still matters for the visible states: text-medium tracks current
    // color (Darker-Blue normally); text-light is Dark-Blue on light surfaces
    // (Figma quirk preserved) but follows currentColor on dark surfaces
    // (color="white") so it renders white.
    ...(isText && {
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 3,
        height: 1,
        backgroundColor:
          $variant === 'text-light' && $color !== 'white'
            ? l.fgSubtleBright
            : 'currentColor',
        opacity: 0,
        transition: 'opacity 120ms ease-out',
      },
      // Hover + Active reveal the underline. Per new-primitive SKILL Step 11:
      // each state activates on its real pseudo-class OR its data-force attr
      // (so the catalog matrix can render either at rest). Real :hover/:active
      // guarded against ALL force-* attrs to prevent stacking on demo cells.
      '&[data-force-hover]::after, &[data-force-active]::after, &:not([data-force-focus]):not([data-force-hover]):not([data-force-active]):hover::after, &:not([data-force-focus]):not([data-force-hover]):not([data-force-active]):active::after': {
        opacity: 1,
      },
    }),

    // Disabled — pointer-events:none paired with aria-disabled on the rendered
    // <span> below (native <a> cannot be truly disabled).
    ...($disabled && {
      pointerEvents: 'none',
    }),
  };
});

// Canonical list lives in Icon.Social.tsx; this alias preserves the local
// SOCIAL_ALLOWED name used below while ensuring the two stay in sync.
const SOCIAL_ALLOWED = SOCIAL_ICON_NAMES;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    variant,
    color = 'dark',
    href,
    disabled = false,
    socialIcon,
    iconName,
    icon,
    target,
    rel,
    children,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  // External-link rel hardening: when target opens a new context, attach
  // noopener + noreferrer (preserving any caller-supplied tokens).
  let computedRel = rel;
  if (target === '_blank') {
    const existing = (rel ?? '').split(/\s+/).filter(Boolean);
    const required = ['noopener', 'noreferrer'];
    const merged = Array.from(new Set([...existing, ...required]));
    computedRel = merged.join(' ');
  }

  // Social-icon variant — narrow socialIcon to one of the asset library's names.
  let leadingIcon: React.ReactNode = null;
  let trailingIcon: React.ReactNode = null;

  if (variant === 'caret-left') {
    leadingIcon = icon ?? <Icon.Standard name={iconName ?? 'chevron-left'} size="sm" />;
  } else if (variant === 'caret-right') {
    trailingIcon = icon ?? <Icon.Standard name={iconName ?? 'chevron-right'} size="sm" />;
  } else if (variant === 'social-icon') {
    if (!socialIcon || !SOCIAL_ALLOWED.includes(socialIcon)) {
      // Surface a hard error rather than rendering a missing icon silently.
      throw new Error(
        `<Link variant="social-icon"> requires socialIcon prop, one of: ${SOCIAL_ALLOWED.join(', ')}`,
      );
    }
    // dev-warn-loud, prod-degrade-gracefully: aria-label is required for the
    // social-icon variant (the icon has no accessible name otherwise), but a
    // missing one shouldn't crash the production render tree. Log loudly in
    // dev; in prod, render the icon without an aria-label rather than throw.
    // (The missing-socialIcon throw above stays — without socialIcon there is
    // nothing to render at all, which is API misuse, not just an a11y gap.)
    if (isDev() && !ariaLabel) {
      console.error(
        `<Link variant="social-icon"> requires aria-label (e.g. "LinkedIn")`,
      );
    }
    leadingIcon = <Icon.Social name={socialIcon} />;
  }

  // Disabled renders as <span aria-disabled="true"> (no href). Otherwise <a>.
  const elementAs = disabled ? 'span' : 'a';
  const elementProps = disabled
    ? { 'aria-disabled': true as const }
    : { href, target, rel: computedRel };

  return (
    <Root
      ref={ref as never}
      as={elementAs as never}
      $variant={variant}
      $color={color}
      $disabled={disabled}
      aria-label={ariaLabel}
      {...(elementProps as object)}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </Root>
  );
});
